import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import DefaultLayout from "../../../../layouts/DefaultLayout";
import { Link, Redirect, withRouter } from "react-router-dom";
import { isNil } from "lodash";
import moment from "moment";
import {
	Button,
	Card,
	Col,
	DatePicker,
	Descriptions,
	message,
	Row,
	Table,
	Typography,
} from "antd";
import {
	defaultCurrencyFormat,
	fetchProtectedResource,
} from "../../../../../utilities";
import { RULES_FILE_VALIDATE_FILE } from "../../../../../configs/api-paths";

/**
 * Results component showing insurance info after user data has been sent to the server.
 */
@inject("context")
@withRouter
@observer
class RateFileQuoteResults extends Component {
	state = {
		rateFileActiveFrom: null,
		rateFileName: this.props.context.insuranceRateQuotingService
			.rateFileSelected?.name,
		rateFileValidated:
			this.props.context.insuranceRateQuotingService.rateFileSelected
				?.activeFrom !== null,
	};

	validateRankingsFile = async () => {
		const {
			insuranceRateQuotingService,
			applicationService,
		} = this.props.context;
		const rateFileId = insuranceRateQuotingService.rateFileSelected.id;
		const validatedBy = applicationService.userProfile.username;
		const activeFrom = this.state.rateFileActiveFrom.toISOString();

		let hideLoadingMessage = message.loading("Validating...", 0);
		try {
			let url = `${RULES_FILE_VALIDATE_FILE.replace("{id}", rateFileId)}`;
			await fetchProtectedResource(url, {
				method: "PATCH",
				body: {
					validatedBy: validatedBy,
					activeFrom: activeFrom,
				},
			});
			hideLoadingMessage();
			message.success(
				"Successfully validated rate file " + this.state.rateFileName,
				5
			);
			this.props.history.push("/rates");
		} catch (error) {
			hideLoadingMessage();
			message.error(
				`Failed validating rate file ${this.state.rateFileName}. Error: ${error}`,
				10
			);
		}
	};

	render() {
		const { insuranceRateQuotingService } = this.props.context;
		const { clientData, insuranceQuote } = insuranceRateQuotingService;
		if (
			Object.entries(clientData).length === 0 &&
			Object.entries(insuranceQuote).length === 0
		) {
			return <Redirect to="/rates" />;
		}

		const dataSource =
			insuranceQuote &&
			insuranceQuote.map((data, index) => {
				let shouldShowQuotations = data.quoteDefer === false;

				return {
					key: index,
					quoteId: data.quoteId,
					insurer: data.insurer,
					quotation:
						shouldShowQuotations && !isNil(data.quotation)
							? data.quotation
							: "",
					terrorismQuotation:
						shouldShowQuotations && !isNil(data.quotation)
							? data.tQuotation
							: "",
					total:
						shouldShowQuotations && !isNil(data.quotation) ? data.total : "",
					ipt_amt:
						shouldShowQuotations && !isNil(data.quotation) ? data.ipt : "",
				};
			});

		const columns = [
			{
				title: "Insurer",
				dataIndex: "insurer",
			},
			{
				title: "Premium",
				dataIndex: "quotation",
				render: (text) => defaultCurrencyFormat(text),
			},
			{
				title: "Terrorism Premium",
				dataIndex: "admin_fee",
				render: (text) => defaultCurrencyFormat(text),
			},
			{
				title: "Total",
				dataIndex: "total",
				render: (text) => defaultCurrencyFormat(text),
			},
			{
				title: "Includes IPT of",
				dataIndex: "ipt_amt",
				render: (text) => defaultCurrencyFormat(text),
			},
			{
				title: "Validate Rate File as of",
				key: "validate",
				render: () => (
					<DatePicker
						showTime={{
							defaultValue: moment("00:00:00", "HH:mm:ss"),
						}}
						onChange={(date) => this.onChangeRateFileActiveFrom(date)}
					/>
				),
			},
			{
				title: "",
				key: "action",
				render: () => (
					<Button
						type="primary"
						disabled={!this.state.rateFileActiveFrom}
						onClick={() => this.validateRankingsFile()}
					>
						Validate Rate File
					</Button>
				),
			},
		];

		const { rateFileName, rateFileValidated } = this.state;
		return (
			<DefaultLayout>
				<Row>
					<Col xs={24}>
						<Card title={<h1>Landlords Insurance - {rateFileName}</h1>}>
							<Row>
								<Col xs={24}>
									<Descriptions
										title={
											<Typography.Title level={4}>
												Your details
											</Typography.Title>
										}
										bordered
										size="small"
										column={1}
									>
										<Descriptions.Item label="Name">
											{clientData.first_name} {clientData.last_name} of{" "}
											{clientData.organisation_name}
										</Descriptions.Item>
										<Descriptions.Item label="Your quote for the property at">
											{[
												clientData.riskaddress1,
												clientData.riskaddress2,
												clientData.riskcity,
												clientData.riskpostcode,
											]
												.filter((el) => el && el.trim())
												.join(", ")}
										</Descriptions.Item>
										<Descriptions.Item label="Re-instatement cost">
											{defaultCurrencyFormat(clientData.declaredvalue).replace(
												/(.)(?=(\d{3})+$)/g,
												"$1,"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Floor material">
											{clientData.woodorconcrete}
										</Descriptions.Item>
										<Descriptions.Item label="PB (purpose built) or CV (converted)?">
											{clientData.pborconverted}
										</Descriptions.Item>
									</Descriptions>

									<Typography.Title style={{ margin: "2rem 0 0 0" }} level={4}>
										Your quote
									</Typography.Title>

									<Table
										dataSource={dataSource}
										columns={columns}
										pagination={false}
										style={{ margin: "2rem 0" }}
									/>

									<Row>
										<Button onClick={this.props.history.goBack}>
											Create another quote
										</Button>
										<Button style={{ margin: "0 0 0 1rem" }}>
											<Link to={rateFileValidated ? "/rates/active" : "/rates"}>
												{rateFileValidated
													? "Back to Active Rates"
													: "Back to Rate Files"}
											</Link>
										</Button>
									</Row>
								</Col>
							</Row>
						</Card>
					</Col>
				</Row>
			</DefaultLayout>
		);
	}

	onChangeRateFileActiveFrom(dateTime) {
		this.setState({
			rateFileActiveFrom: dateTime,
		});
	}
}

export default RateFileQuoteResults;
